import {Component, ElementRef, OnInit, ViewChild} from "@angular/core";
import {CKEditorConfig, DocInfo} from "../../../models/template";
import {TemplateService} from "../../../services/template.service";
import {MatSelectChange} from "@angular/material/select";
import {ClassicEditor} from 'ckeditor5';
import {CKEditorComponent} from "@ckeditor/ckeditor5-angular";
import {UtilService} from "../../../services/util.service";
import {finalize} from "rxjs";


@Component({
    selector: 'app-edit-templates',
    templateUrl: './edit-templates.component.html',
    styleUrls: ['./edit-templates.component.scss'],
    standalone: false
})
export class EditTemplatesComponent implements OnInit {

  @ViewChild('printDataContainer', { static: true }) printIframe!: ElementRef<HTMLIFrameElement>;
  @ViewChild('editor', { static: false }) editor!: CKEditorComponent;

  protected readonly ClassicEditor = ClassicEditor;
  protected readonly CKEditorConfig = CKEditorConfig;

  filteredDocList: DocInfo[] = [];
  docGroups: string[] = [];
  documentsByGroup = new Map<string, DocInfo[]>();
  isEditorVisible = false;
  isReviewVisible = false;
  editorContent?: string;
  currentDocInfo?: DocInfo;
  docFileName?: string;
  selectedDocType?: number;
  saving = false;

  constructor(private templateService: TemplateService,
              private utilService: UtilService) {
  }

  ngOnInit() {
    this.getTemplates();
  }

  getTemplates() {
    this.templateService.getTemplates().subscribe(templates => {

      if (templates.authorized) {
        this.filteredDocList = this.filterDocInfoListByTemplateDocIds(templates.docInfoList);
        this.separateByGroups();
        this.docGroups = Array.from(this.documentsByGroup.keys());
      } else {
        console.debug(templates);
        this.utilService.displayDialogMessage("Unauthorized", "You are not authorized.");
      }
    })
  }

  filterDocInfoListByTemplateDocIds(docInfoList: DocInfo[]) {
    const listOfDocIds = [103,104,135,106,116,126,107,127,136,117,112,122,131,140,105,114,115,125,134,124,133,137,128,119,109,191,193,195,197,235,236,237,238,110,120,138,129];

    const filteredDocInfoList = docInfoList.filter(item => {
      return listOfDocIds.includes(item.doc_Type_Id);
    });

    return filteredDocInfoList;
  }

  separateByGroups() {
    this.filteredDocList.forEach(item => {
      const group = this.documentsByGroup.get(item.case_Type) ?? [];
      group.push(item);
      this.documentsByGroup.set(item.case_Type, group);
    });
  }

  onDocTypeChange(event: MatSelectChange) {
    this.currentDocInfo = event.value;
    const docTypeId = this.currentDocInfo?.doc_Type_Id;

    if (docTypeId) {
      this.templateService.generateTemplateBasedOnDocumentType(docTypeId).subscribe(templateDetails => {
        this.editorContent = templateDetails.htmlContent;
        this.isEditorVisible = true;
      });
    }
  }

  onEditorSave() {
    this.editorContent = this.editor.editorInstance?.getData()
    const docTypeId = this.currentDocInfo?.doc_Type_Id;

    if (this.editorContent && docTypeId) {
      this.saving = true;
      this.templateService.createPdf(this.editorContent, docTypeId)
        .pipe(finalize(() => this.saving = false))
        .subscribe(doc => {
          this.docFileName = doc.fileName + '.pdf';
          this.isEditorVisible = false;
          this.isReviewVisible = true;
          this.utilService.displayDialogMessage("Review", "You can review the edits by downloading the PDF file before saving. Please save edits once your review is complete.");
      });
    } else {
      console.error(this.editorContent);
      console.error(docTypeId);
      this.utilService.displayDialogMessage("Error", "There was a problem saving the template updates.");
    }
  }

  onEditorCancel() {
    this.isEditorVisible = false;
    this.selectedDocType = undefined;
  }

  onPrintPreview() {
    this.printIframe.nativeElement.srcdoc = '<html>' +
      '<head>' +
      `<title>${ document.title }</title>` +
      '</head>' +
      '<body class="ck-content">' +
      this.editor.editorInstance?.getData() +
      '<script>' +
      'window.addEventListener( \'DOMContentLoaded\', () => { window.print(); } );' +
      '</script>' +
      '</body>' +
      '</html>';
  }

  onViewDocument() {
    const docTypeId = this.currentDocInfo?.doc_Type_Id;
    const filename = this.docFileName;

    if (filename && docTypeId) {
      this.templateService.downloadTempPdf(filename, docTypeId);
    } else {
      console.error(docTypeId);
      console.error(filename);
      this.utilService.displayDialogMessage("Error", "There was a problem generating the temporary PDF.");
    }
  }

  onSaveEdits() {
    const docTypeId = this.currentDocInfo?.doc_Type_Id;

    if (this.editorContent && docTypeId) {
      this.templateService.saveTemplate(this.editorContent, docTypeId).subscribe(() => {
        this.utilService.displayDialogMessage("Success", "Your edits have been saved.").subscribe(() => {
          this.selectedDocType = undefined;
          this.isReviewVisible = false;
        });
      });
    } else {
      console.error(docTypeId);
      console.error(this.editorContent);
      this.utilService.displayDialogMessage("Error", "There was a problem saving the template.");
    }
  }

  onClearEdits() {
    this.selectedDocType = undefined;
    this.isReviewVisible = false;
  }

  onEditTemplate() {
    this.isReviewVisible = false;
    this.isEditorVisible = true;
  }

}
